import { useCallback, useMemo, useState } from 'react'
import { MODAL_IDS, useModal } from 'src/components'
import { useIsMobile } from 'src/hooks'
import { TerminalItem } from 'src/schema'

import { DEFAULT_CENTER, DEFAULT_ZOOM, DEFAULT_ZOOM_MOBILE, ZOOM_BY_POINT, ZOOM_BY_POINT_MOBILE } from './constants'
import { SideBarMode } from './types'

export const useYarcheAddressSelect = ({
  modalId,
  terminalId,
  changeTerminalId,
  terminals
}: {
  changeTerminalId: ({ terminalId }: { terminalId: TerminalItem['id'] | undefined }) => void
  modalId: MODAL_IDS.YarcheDepartureSelectMap | MODAL_IDS.YarcheDestionationSelectMap
  terminalId: TerminalItem['id'] | undefined
  terminals: TerminalItem[] | undefined
}) => {
  const { closeModal } = useModal()

  const terminalByField = useMemo(
    () => (terminals || []).find((item) => item.id === terminalId),
    [terminalId, terminals]
  )

  const isMobile = useIsMobile()
  const [sideBarMode, setSideBarMode] = useState<SideBarMode>('list')
  const [center, setCenter] = useState(DEFAULT_CENTER)
  const [zoom, setZoom] = useState(DEFAULT_ZOOM)
  const [confirmedTerminal, setConfirmedTerminal] = useState<TerminalItem | null>(null)
  const [selectedTerminal, setSelectedTerminal] = useState<TerminalItem | null>(null)

  const zoomByVieport = useMemo(() => {
    if (isMobile) {
      if (zoom === DEFAULT_ZOOM) {
        return DEFAULT_ZOOM_MOBILE
      }
      if (zoom === ZOOM_BY_POINT) {
        return ZOOM_BY_POINT_MOBILE
      }
    }

    return zoom
  }, [zoom, isMobile])

  const centerByVieport = useMemo(() => {
    if (isMobile) {
      return [center[0] - 0.0012, center[1]]
    }

    return [center[0], center[1] + 0.005]
  }, [center, isMobile])

  const resetCenter = useCallback(() => {
    setCenter(DEFAULT_CENTER)
    setZoom(DEFAULT_ZOOM)
  }, [])

  const clear = useCallback(() => {
    resetCenter()
    setConfirmedTerminal(null)
    setSelectedTerminal(null)
    setSideBarMode('list')
  }, [])

  const handleCloseMapModal = useCallback(() => {
    closeModal(modalId)
    clear()
  }, [clear, closeModal, modalId])

  const handleSetCenter = useCallback(
    (latitude: number | string | undefined, longitude: number | string | undefined) => {
      setCenter([Number(latitude), Number(longitude)])
      setZoom(ZOOM_BY_POINT)
    },
    []
  )

  const handleChangeSelectedTerminal = useCallback(
    (option: TerminalItem['id'] | TerminalItem | null, changeMode: boolean = true) => {
      const terminal = typeof option === 'string' ? (terminals || []).find((item) => item.id === option) : option
      setSelectedTerminal(terminal || null)

      handleSetCenter(terminal?.latitude, terminal?.longitude)

      if (changeMode) {
        setSideBarMode('detail')
      }
    },
    [terminals]
  )

  const handleChangeConfirmedTerminal = useCallback(() => {
    changeTerminalId({ terminalId: selectedTerminal?.id })
    handleCloseMapModal()
  }, [handleCloseMapModal, changeTerminalId, selectedTerminal])

  const removeSelectedItems = useCallback(() => {
    setSelectedTerminal(null)
    setConfirmedTerminal(null)
    resetCenter()
  }, [])

  return {
    terminalByField,
    sideBarMode,
    setSideBarMode,
    removeSelectedItems,
    handleCloseMapModal,
    handleChangeConfirmedTerminal,
    handleChangeSelectedTerminal,
    zoom: zoomByVieport,
    clear,
    center: centerByVieport,
    confirmedTerminal,
    selectedTerminal
  }
}
